import { DEFAULT_QA_SCREENSHOTS, DEFAULT_QA_RECORDING } from "@yagni/shared";
/**
 * One reading of a configuration, used everywhere the product has to say what
 * it worked out about a repository: the expandable detail on the inspect leg
 * of a setup run, and Fletcher's proposal on the work record.
 *
 * There used to be two of these, a definition list and a comparison table, and
 * they drifted. One component now, so what a reader is shown after a run is
 * exactly what they are shown when a proposal lands.
 */
import {
  DEFAULT_QA_COMMAND_TIMEOUT_MS,
  DEFAULT_QA_READINESS_TIMEOUT_MS,
  type QaEnvironmentConfig,
  type QaSetupReport,
} from "@yagni/shared";
import { Markdown } from "../components/Markdown.js";

/** "1 service" / "2 services", never "1 service(s)". */
export function count(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? "" : "s"}`;
}

/** A one-line reading of what the inspector recovered. */
export function inspectionSummary(config: QaEnvironmentConfig): string {
  return [
    config.setupCommands.filter(Boolean)[0],
    config.services.length ? count(config.services.length, "service") : null,
    config.verifyCommand,
  ]
    .filter(Boolean)
    .join(" · ");
}

export function evidenceSummary(config: QaEnvironmentConfig): string {
  const screenshots = config.screenshots ?? DEFAULT_QA_SCREENSHOTS;
  const recording = config.recording ?? DEFAULT_QA_RECORDING;
  return `${screenshots.maxCount.toLocaleString()} screenshots · ${screenshots.maxBytes / (1024 * 1024)} MiB · ${recording.maxSeconds / 60} min video · ${recording.maxBytes / (1024 * 1024)} MiB`;
}

export function EnvironmentConfigSummary({ config }: { config: QaEnvironmentConfig }) {
  return (
    <dl className="qa-summary">
      <dt>Installation</dt>
      <dd>
        <pre>{config.setupCommands.filter(Boolean).join("\n") || "No installation commands"}</pre>
      </dd>
      {config.services.map((service, index) => (
        <div key={`${service.name}-${index}`}>
          <dt>
            {service.name || "Service"} · port {service.port}
          </dt>
          <dd>
            <code>{service.command}</code>
            <p>
              Directory {service.cwd} · Readiness {service.readinessPath} · Startup wait{" "}
              {(service.readinessTimeoutMs ?? DEFAULT_QA_READINESS_TIMEOUT_MS) / 60000} minutes
            </p>
          </dd>
        </div>
      ))}
      <dt>Browser application</dt>
      <dd>{config.browserService || "The first configured service"}</dd>
      <dt>Entry path</dt>
      <dd>{config.entryPath}</dd>
      <dt>Temporary database</dt>
      <dd>
        {config.postgres
          ? `PostgreSQL 17 with vector support on port ${config.postgres.port}`
          : "Not requested"}
      </dd>
      <dt>Command wait</dt>
      <dd>
        {(config.commandTimeoutMs ?? DEFAULT_QA_COMMAND_TIMEOUT_MS) / 60000} minutes per command,
        within the run deadline
      </dd>
      <dt>Evidence limits</dt>
      <dd>{evidenceSummary(config)}</dd>
      <dt>Verification</dt>
      <dd>{config.verifyCommand ?? "No verification command"}</dd>
      <dt>Browser tests</dt>
      <dd>{config.browserTestCommand ?? "No browser test command"}</dd>
      <dt>Sign-in</dt>
      <dd>
        <code>{config.authCommand ?? "No bootstrap command"}</code>
        <p>Signed-in state: {config.authCheckSelector ?? "No selector configured"}</p>
      </dd>
      <dt>Approved destinations</dt>
      <dd>{config.allowedOrigins.filter(Boolean).join(", ") || "No external app destinations"}</dd>
      <dt>Hidden sensitive elements</dt>
      <dd>{config.maskSelectors.filter(Boolean).join(", ") || "Default sensitive fields only"}</dd>
      <dt>Browser size</dt>
      <dd>
        {config.viewport.width} × {config.viewport.height}
      </dd>
      <dt>Target</dt>
      <dd>{config.target === "sandbox" ? "Disposable sandbox" : "Matching deployment preview"}</dd>
    </dl>
  );
}

/**
 * Fletcher's proposal on a work record: the explanation, what still needs a
 * person, the same configuration summary, and the trials it took to get there.
 */
export function EnvironmentSetupResult({ report }: { report: QaSetupReport }) {
  return (
    <section className="qa-proposal" aria-label="Fletcher setup proposal">
      <h3>Fletcher&rsquo;s setup proposal</h3>
      <Markdown text={report.explanation} />
      <p className="qa-fact">Inspected revision {report.sourceSha}</p>
      {report.missingPrerequisites.length > 0 && (
        <div>
          <h4>Needs your attention</h4>
          <ul>
            {report.missingPrerequisites.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>
      )}
      <details>
        <summary>Review proposed commands</summary>
        <EnvironmentConfigSummary config={report.config} />
      </details>
      {report.trials.map((trial) => (
        <div key={trial.attempt}>
          <h4>
            Trial {trial.attempt} · {trial.status === "ready" ? "Working" : "Needs attention"}
          </h4>
          <p>{trial.message}</p>
        </div>
      ))}
      {report.captures.at(-1) && (
        <a href={report.captures.at(-1)!.url} target="_blank" rel="noreferrer">
          <img
            src={report.captures.at(-1)!.url}
            alt={"Browser view from Fletcher's latest setup trial"}
          />
        </a>
      )}
      <p>
        Accepting creates a draft. Validate it, then choose Use this environment to activate it.
      </p>
    </section>
  );
}
